import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { RxAxios, RxAxiosConfig } from './rxAxios';
import { NetworkHelper } from './helper';
import { BaseUrls } from './baseUrl';
import { Environment } from 'types';
import store from '../../store';

export interface BuilderI {
  request: boolean;
  response: boolean;
}

export class ApiModule {
  private static instance: ApiModule;

  apiML: RxAxios;

  constructor() {
    this.apiML = this.mlModuleBuilder(BaseUrls.mlApiUrl);
  }

  params = {
    request: true,
    response: true,
  };

  builder = (
    url: string,
    interceptors: BuilderI = { request: true, response: true },
  ) => {
    const configs: RxAxiosConfig = {
      options: {
        baseURL: url,
      },
    };

    if (interceptors.request) {
      configs.interceptors = {
        request: this.onRequest,
      };
    }

    if (interceptors.response && configs.interceptors) {
      configs.interceptors.response = {
        fulfilled: this.onResponse,
        error: this.onError,
      };
    }

    return new RxAxios(configs);
  };

  public static get(): ApiModule {
    if (!ApiModule.instance) {
      ApiModule.instance = new ApiModule();
    }
    return ApiModule.instance;
  }

  onRequest = async (res: AxiosRequestConfig) => {
    res.headers['Content-Type'] = 'application/json';

    const session = NetworkHelper.getAuthCredentials();

    res.headers['x-locale'] = NetworkHelper.getLocale();
    res.headers['x-currency'] = NetworkHelper.getCurrency().currency;
    res.headers['x-real-status-code'] = false;
    res.headers['x-host'] = window.location.host;

    if (session && session.sessionId && !session.legacy) {
      res.headers[NetworkHelper.SESSIONID] = session.sessionId;
    }

    if (session && session.sessionId && session.userId && session.legacy) {
      res.headers[NetworkHelper.LEGACY_SESSION] = session.sessionId;
      res.headers[NetworkHelper.LEGACY_USERID] = session.userId;
    }

    return res;
  };

  onError = (err: AxiosResponse<any>) => {
    return this.sessionInterceptor(err, false);
  };

  onResponse = (res: AxiosResponse) => {
    return this.sessionInterceptor(res, true);
  };

  sessionInterceptor = async (_res: AxiosResponse<any>, isSuccess: boolean) => {
    if (!_res) {
      return Promise.reject({
        header: 'NETWORK_ERROR',
        message: 'Network error',
      });
    }

    const res = isSuccess ? _res : _res['response'];

    if (!res) {
      return Promise.reject({
        header: 'NETWORK_ERROR',
        message: 'Network error',
      });
    }

    const originalRequest = res.config;

    const isSessionExpired = (it: AxiosResponse<any>): boolean => {
      const legacy =
        (it &&
          it.data &&
          it.data['error'] &&
          it.data['error'] === 'SESSION_NOT_FOUND') ||
        it.data['error'] === 'SESSION_WRONG';

      const ml = it.status === 401;

      return legacy || ml;
    };

    if (isSessionExpired(res)) {
      store.dispatch('user/invalidateUser');

      delete originalRequest.headers[NetworkHelper.LEGACY_SESSION];
      delete originalRequest.headers[NetworkHelper.SESSIONID];
      delete originalRequest.headers[NetworkHelper.LEGACY_USERID];

      return axios(originalRequest);
    } else {
      return isSuccess ? res : Promise.reject(res);
    }
  };

  mlModuleBuilder = (url: string) => {
    const authKey = ApiModule.getMLAuthKey();

    const configs: RxAxiosConfig = {
      options: {
        baseURL: url + '/api/v1',
      },
    };

    const _onRequest = async (res: AxiosRequestConfig) => {
      res.headers['authorization'] = authKey;
      return this.onRequest(res);
    };

    configs.interceptors = {
      request: _onRequest,
    };

    configs.interceptors.response = {
      fulfilled: this.onResponse,
      error: this.onError,
    };

    return new RxAxios(configs);
  };

  static getMLAuthKey(): string {
    const env = BaseUrls.getEnv(true);
    return env === Environment.Production || env === Environment.Stage
      ? process.env.ML_AUTH_KEY ?? 'dev'
      : 'dev';
  }
}
